import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

interface HighScoreDisplayProps {
  highScore: number;
  isNewHighScore: boolean;
  onAnimationComplete?: () => void;
}

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const sparkle = keyframes`
  0% {
    background-position: 0% 50%;
    opacity: 0.8;
  }
  100% {
    background-position: 100% 50%;
    opacity: 0.8;
  }
`;

const HighScoreContainer = styled.div<{ isNewHighScore: boolean }>`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  height: 30px;
  background-color: ${props => props.isNewHighScore ? 'transparent' : 'rgba(0, 0, 0, 0.6)'};
  color: ${props => props.isNewHighScore ? '#FFD700' : 'white'};
  padding: 6px 10px;
  border-radius: 20px;
  font-weight: bold;
  font-size: 14px;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${props => props.isNewHighScore ? css`${pulse} 0.5s ease-in-out 3` : 'none'};
  
  ${props => props.isNewHighScore && css`
    background: linear-gradient(90deg, #FFD700, #FFA500, #FF8C00, #FFD700);
    background-size: 300% 300%;
    animation: ${pulse} 0.5s ease-in-out 3, ${sparkle} 2s linear infinite;
    box-shadow: 0 0 10px 2px rgba(255, 215, 0, 0.7);
    border: 2px solid #FFD700;
  `}
  
  @media (max-height: 700px) {
    font-size: 12px;
    padding: 4px 8px;
  }
  
  @media (max-height: 600px) {
    font-size: 10px;
    padding: 3px 6px;
  }
`;

const Trophy = styled.span`
  margin-right: 5px;
  font-size: 18px;
`;

const HighScoreDisplay: React.FC<HighScoreDisplayProps> = ({ 
  highScore, 
  isNewHighScore,
  onAnimationComplete 
}) => {
  const [showAnimation, setShowAnimation] = useState(isNewHighScore);
  
  useEffect(() => {
    setShowAnimation(isNewHighScore);
    
    if (isNewHighScore) {
      const timer = setTimeout(() => {
        setShowAnimation(false);
        if (onAnimationComplete) {
          onAnimationComplete();
        }
      }, 3000); // 3 seconds for the animation
      
      return () => clearTimeout(timer);
    }
  }, [isNewHighScore, onAnimationComplete]);
  
  return (
    <HighScoreContainer isNewHighScore={showAnimation}>
      <Trophy>🏆</Trophy>
      {showAnimation ? 'NEW HIGH SCORE: ' : 'HIGH SCORE: '}{highScore}
    </HighScoreContainer>
  );
};

export default HighScoreDisplay;
